import { useAuth as useOidcAuth } from 'react-oidc-context';

/**
 * Main authentication hook for accessing Keycloak authentication state and methods
 *
 * This hook provides access to the current user, authentication state, and methods
 * for signing in and out. It must be used within a KeycloakProvider component.
 *
 * @returns Authentication state and methods
 *
 * @example
 * ```tsx
 * import { useAuth } from 'antaeus.keycloak.react';
 *
 * function MyComponent() {
 *   const auth = useAuth();
 *
 *   if (auth.isLoading) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   if (!auth.isAuthenticated) {
 *     return <button onClick={() => auth.signinRedirect()}>Login</button>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {auth.user?.profile?.name}</p>
 *       <button onClick={() => auth.signoutRedirect()}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Role checking
 * ```tsx
 * function AdminPanel() {
 *   const auth = useAuth();
 *   const roles = auth.user?.profile?.realm_access?.roles || [];
 *
 *   if (!roles.includes('admin')) {
 *     return <div>Access denied</div>;
 *   }
 *
 *   return <div>Admin panel content</div>;
 * }
 * ```
 */
export const useAuth = useOidcAuth;

export type {
  /**
   * Authentication context returned by useAuth
   */
  AuthContextProps as UseAuthReturn,
} from 'react-oidc-context';
